import { useGlobalState } from '../GlobalState';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Button } from 'antd';
import { costString } from '../../../../utils/cost';
import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { useConfirmPaymentIntentMutation } from '../../paymentsSlice';
import PaymentFailure from './PaymentFailure';

function formatStripeError(err) {
  return {
    message: 'Payment Failed',
    payment_status: err?.type ?? 'Error',
    payment_succeeded: false,
    payment_status_detail: err?.message,
  };
}

function StripePaymentForm({
  idField = { name: 'booking_public_id', value: 0 },
  billingDetails = {},
  amount,
  onPaymentSuccess,
  onPaymentFailure,
}) {
  const stripe = useStripe();
  const elements = useElements();

  const paymentFormWrapperRef = useRef();
  const [, setPaymentSuccessRedirect] = useGlobalState(
    'paymentSuccessRedirect'
  );
  const [, setBookingTrialIssued] = useGlobalState('bookingTrialIssued');
  const [localError, setLocalError] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);

  const [confirmPaymentIntent, { isFetching: submitting }] =
    useConfirmPaymentIntentMutation();
  const [isCreatingToken, setIsCreatingToken] = useState(false);
  const loading = isCreatingToken || submitting;

  const confirmPayment = useCallback(
    async (e) => {
      setIsCreatingToken(true);

      // Prevent default form submission (page refresh) - defer to Stripe instead
      e.preventDefault();
      if (!stripe || !elements) {
        // Stripe.js hasn't loaded yet
        return;
      }

      // Validate form
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setIsCreatingToken(false);
        return;
      }

      // Create confirmation token
      let tokenError;
      let confirmationToken = null;
      try {
        const { error, confirmationToken: ct } =
          await stripe.createConfirmationToken({
            elements,
            params: {
              payment_method_data: {
                billing_details: {
                  email: billingDetails?.email?.trim?.()?.toLowerCase?.(),
                },
              },
              return_url: undefined,
            },
          });
        confirmationToken = ct;
        tokenError = error;
      } catch (err) {
        tokenError = err;
      }
      if (tokenError) {
        // Error when creating token
        setLocalError(formatStripeError(tokenError));
        setIsCreatingToken(false);
        return;
      }

      const data = {
        [idField.name]: idField.value,
        confirmation_token_id: confirmationToken.id,
        payment_intent_id: paymentIntentId,
      };

      setLocalError(null);
      setPaymentSuccessRedirect(null);
      setBookingTrialIssued(false);

      confirmPaymentIntent(data)
        .unwrap()
        .then((response) => {
          setPaymentSuccessRedirect(response?.redirect);
          setPaymentIntentId(response?.payment_intent_id);
          if (response?.trial_issued !== undefined) {
            setBookingTrialIssued(Boolean(response.trial_issued));
          }
          if (response.status === 'requires_action') {
            // E.g. 3DS verification
            return stripe.handleNextAction({
              clientSecret: response.client_secret,
            });
          } else {
            return { success: true };
          }
        })
        .then((result) => {
          if (result?.error) {
            throw result.error;
          } else {
            onPaymentSuccess();
          }
        })
        .catch((err) => {
          const piid =
            err?.data?.detail?.payment_intent_id ?? err?.payment_intent_id;
          if (piid) {
            setPaymentIntentId(piid);
          }
          setLocalError(err?.data?.detail ?? formatStripeError(err));
          onPaymentFailure();
        })
        .finally(() => {
          setIsCreatingToken(false);
        });
    },
    [
      stripe,
      elements,
      billingDetails,
      idField.name,
      idField.value,
      paymentIntentId,
      setPaymentSuccessRedirect,
      setBookingTrialIssued,
      confirmPaymentIntent,
      onPaymentSuccess,
      onPaymentFailure,
    ]
  );

  const paymentForm = useMemo(
    () => (
      <form onSubmit={confirmPayment}>
        <PaymentElement
          className='mg-bottom-20'
          options={{
            defaultValues: {
              billingDetails: {
                name: billingDetails?.name,
              },
            },
            fields: {
              billingDetails: {
                name: 'auto',
                email: 'auto',
              },
            },
            layout: {
              type: 'tabs',
              defaultCollapsed: false,
            },
          }}
        />
        <Button
          id='payment_button'
          className={
            'payment_form__submit sb-blue-bg white w-full gSemiBold fs-20 pd-10 h-full br5 mg-top-10'
          }
          htmlType='submit'
          loading={loading}
          disabled={!stripe || loading}
        >
          Pay {amount ? costString(amount) : '£unknown'}
        </Button>
      </form>
    ),
    [amount, confirmPayment, loading, stripe]
  );

  return (
    <div ref={paymentFormWrapperRef} className='pd-top-10 pd-bottom-20'>
      <div className={localError ? 'hide' : 'show'}>{paymentForm}</div>
      {localError && (
        <PaymentFailure
          errorPayment={localError}
          retryPaymentButton={
            <Button
              className={
                'sb-blue-bg gSemiBold br10 fs-20 white txt-center w-full pd-10 mg-top-10 h-fit antd-link-button-override'
              }
              onClick={() => setLocalError(null)}
            >
              &lt; Back to payment
            </Button>
          }
        />
      )}
    </div>
  );
}

export default StripePaymentForm;
